import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PlayerFormDialog } from '@/components/features/players/PlayerFormDialog';
import { PlayerSelectionCard } from '@/components/features/players/PlayerSelectionCard';
import { TeamConfigurationStep } from '@/components/features/sessions/create/TeamConfigurationStep';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { listPlayers } from '@/db/players';
import { createSessionTemplate } from '@/db/session-templates';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFormErrorScroll } from '@/hooks/useFormErrorScroll';
import { toastError, toastPromise } from '@/lib/toast-helpers';
import { parsePlayerId, parseTeamId, type GameConfig, type Player, type PlayerId } from '@/schemas';
import { useAuthStore } from '@/stores';

const optionalPositiveNumber = z
  .union([z.number().int().positive(), z.nan()])
  .transform((val): number | undefined => {
    if (Number.isNaN(val)) {
      return undefined;
    }
    return val;
  })
  .optional();

const NewSessionTemplateSchema = z.object({
  name: z
    .string()
    .min(1, 'Template name is required')
    .max(200, 'Name must be 200 characters or less'),
  description: z.string().max(2000, 'Description must be 2000 characters or less').optional(),
  defaultSessionName: z
    .string()
    .max(200, 'Default session name must be 200 characters or less')
    .optional(),
  // Game Config fields
  hasRounds: z.boolean(),
  winCondition: z.enum(['mostPoints', 'mostRounds']),
  roundTargetPoints: optionalPositiveNumber,
  gameTargetPoints: optionalPositiveNumber,
  targetRounds: optionalPositiveNumber,
  teamScoringType: z.union([z.enum(['combined', 'individual']), z.literal('none')]).optional(),
  // Player selection
  playerIds: z.array(z.string()).min(1, 'At least one player is required'),
  // Team configuration (will be built from form state)
});

type NewSessionTemplateForm = z.infer<typeof NewSessionTemplateSchema>;

interface TeamConfig {
  readonly id: string;
  readonly presetId?: string;
  readonly name?: string;
  readonly playerIds: readonly PlayerId[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
export const Route = createFileRoute('/templates/new' as any)({
  component: NewSessionTemplatePage,
});

function NewSessionTemplatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('sessions');
  const { t: tCommon } = useTranslation('common');
  const userId = useAuthStore((state) => state.userId);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPlayerDialog, setShowPlayerDialog] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState<PlayerId | undefined>();
  const [teamConfiguration, setTeamConfiguration] = useState<readonly TeamConfig[]>([]);

  useDocumentTitle(
    `${t('templates.createButton', 'Create Template')} - ${tCommon('app.name', 'One Score')}`,
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<NewSessionTemplateForm>({
    resolver: zodResolver(NewSessionTemplateSchema),
    defaultValues: {
      hasRounds: false,
      winCondition: 'mostPoints',
      playerIds: [],
      teamScoringType: 'none',
    },
  });

  useFormErrorScroll(errors);

  const watchedHasRounds = useWatch({ control, name: 'hasRounds' });
  const watchedWinCondition = useWatch({ control, name: 'winCondition' });
  const watchedTeamScoringType = useWatch({ control, name: 'teamScoringType' });
  const watchedPlayerIds = useWatch({ control, name: 'playerIds' });
  const watchedName = useWatch({ control, name: 'name' });
  const watchedDescription = useWatch({ control, name: 'description' });

  // Auto-set winCondition when hasRounds changes
  useEffect(() => {
    if (!watchedHasRounds) {
      setValue('winCondition', 'mostPoints');
    }
  }, [watchedHasRounds, setValue]);

  // Load players
  useEffect(() => {
    void loadPlayers();

    const handleFocus = () => {
      void loadPlayers();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadPlayers = async () => {
    try {
      setIsLoadingPlayers(true);
      const players = await listPlayers();
      setAllPlayers(players);
    } catch (error: unknown) {
      console.error('Failed to load players:', error);
    } finally {
      setIsLoadingPlayers(false);
    }
  };

  // Filter out selected players from available list
  const availablePlayers = allPlayers.filter((p) => !watchedPlayerIds?.includes(String(p.id)));

  const filteredAvailablePlayers = availablePlayers.filter((player) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      player.name.toLowerCase().includes(query) || player.nickname?.toLowerCase().includes(query)
    );
  });

  const selectedPlayers = allPlayers.filter((p) => watchedPlayerIds?.includes(String(p.id)));

  const handlePlayerToggle = (playerId: PlayerId) => {
    const currentPlayerIds = watchedPlayerIds ?? [];
    const playerIdString = String(playerId);
    if (!currentPlayerIds.includes(playerIdString)) {
      setValue('playerIds', [...currentPlayerIds, playerIdString]);
    }
  };

  const handleRemovePlayer = (playerId: PlayerId) => {
    const currentPlayerIds = watchedPlayerIds ?? [];
    const playerIdString = String(playerId);
    setValue(
      'playerIds',
      currentPlayerIds.filter((id) => id !== playerIdString),
    );
  };

  const handleEditPlayer = (playerId: PlayerId) => {
    setEditingPlayerId(playerId);
    setShowPlayerDialog(true);
  };

  const onSubmit = async (data: NewSessionTemplateForm) => {
    setIsCreating(true);
    setError(undefined);

    try {
      const gameConfig: GameConfig = {
        hasRounds: data.hasRounds,
        winCondition: data.winCondition,
        roundTargetPoints: data.roundTargetPoints,
        gameTargetPoints: data.gameTargetPoints,
        targetRounds: data.targetRounds,
        teamScoringType: data.teamScoringType === 'none' ? undefined : data.teamScoringType,
        version: 1,
      };

      const playerIds: PlayerId[] = data.playerIds.map((id) => parsePlayerId(id));

      // Build team configuration if teams are enabled
      const teamConfig =
        gameConfig.teamScoringType && teamConfiguration.length > 0
          ? {
              teams: teamConfiguration.map((team) => ({
                id: parseTeamId(team.id),
                presetId: team.presetId ? parseTeamId(team.presetId) : undefined,
                name: team.name,
                nickname: undefined,
                avatar: undefined,
                color: undefined,
                playerIds: [...team.playerIds],
              })),
            }
          : undefined;

      await toastPromise(
        createSessionTemplate({
          name: data.name.trim(),
          description: data.description?.trim() || undefined,
          gameConfig,
          playerIds,
          teamConfiguration: teamConfig,
          defaultSessionName: data.defaultSessionName?.trim() || undefined,
          userId: userId ?? undefined,
        }),
        {
          loading: t('templates.create.loading', 'Creating template...'),
          success: t('templates.create.success', 'Template created successfully'),
          error: (error: unknown) =>
            error instanceof Error
              ? error.message
              : t('templates.create.error', 'Failed to create template'),
        },
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
      void navigate({ to: '/templates' as any });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('templates.create.error');
      setError(errorMessage);
      toastError(errorMessage);
      setIsCreating(false);
    }
  };

  const hasTeams = watchedTeamScoringType && watchedTeamScoringType !== 'none';

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('templates.createButton', 'Create Template')}
          </h1>
          <p className="text-muted-foreground">
            {t('templates.create.description', 'Create a reusable session template')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Route tree will be regenerated after build
          onClick={() => void navigate({ to: '/templates' as any })}
          disabled={isCreating}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {tCommon('back', 'Back')}
        </Button>
      </div>

      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-6">
        {/* Template Info Section */}
        <Card className="p-6">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">
              {t('templates.create.templateInfo', 'Template Information')}
            </h2>
            <div className="space-y-2">
              <Label htmlFor="template-name">{t('templates.create.name', 'Template Name')} *</Label>
              <Input
                id="template-name"
                placeholder={t('templates.create.namePlaceholder', 'Enter template name')}
                {...register('name')}
                disabled={isCreating}
                maxLength={200}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              {200 - (watchedName?.length ?? 0) < 20 && (
                <p className="text-xs text-muted-foreground">
                  {tCommon('form.charactersRemaining', {
                    count: 200 - (watchedName?.length ?? 0),
                  })}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">
                {t('templates.create.descriptionLabel', 'Description')}
              </Label>
              <Textarea
                id="template-description"
                placeholder={t(
                  'templates.create.descriptionPlaceholder',
                  'Optional description...',
                )}
                rows={3}
                {...register('description')}
                disabled={isCreating}
                maxLength={2000}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
              {2000 - (watchedDescription?.length ?? 0) < 20 && (
                <p className="text-xs text-muted-foreground">
                  {tCommon('form.charactersRemaining', {
                    count: 2000 - (watchedDescription?.length ?? 0),
                  })}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="default-session-name">
                {t('templates.create.defaultSessionName', 'Default Session Name')}
              </Label>
              <Input
                id="default-session-name"
                placeholder={t(
                  'templates.create.defaultSessionNamePlaceholder',
                  'Optional default name for new sessions...',
                )}
                {...register('defaultSessionName')}
                disabled={isCreating}
                maxLength={200}
              />
              {errors.defaultSessionName && (
                <p className="text-sm text-destructive">{errors.defaultSessionName.message}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Game Configuration Section */}
        <Card className="p-6">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">
              {t('templates.create.gameConfig', 'Game Configuration')}
            </h2>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasRounds"
                checked={watchedHasRounds}
                onCheckedChange={(checked) => {
                  setValue('hasRounds', checked === true);
                }}
                disabled={isCreating}
              />
              <Label htmlFor="hasRounds" className="cursor-pointer font-normal">
                {tCommon('games.form.hasRounds', 'Has Rounds')}
              </Label>
            </div>
            <p className="text-sm text-muted-foreground ml-6">
              {tCommon('games.form.hasRounds.desc', 'Track scores per round')}
            </p>

            {watchedHasRounds && (
              <div className="space-y-4 ml-6">
                <Label>{tCommon('games.form.winCondition', 'Win Condition')} *</Label>
                <RadioGroup
                  value={watchedWinCondition}
                  onValueChange={(value) => {
                    setValue('winCondition', value as 'mostPoints' | 'mostRounds', {
                      shouldValidate: true,
                    });
                  }}
                  disabled={isCreating}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="mostPoints" id="win-most-points" />
                    <Label htmlFor="win-most-points" className="cursor-pointer font-normal">
                      {tCommon('games.form.winCondition.mostPoints', 'Most Points')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="mostRounds" id="win-most-rounds" />
                    <Label htmlFor="win-most-rounds" className="cursor-pointer font-normal">
                      {tCommon('games.form.winCondition.mostRounds', 'Most Rounds')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-lg font-semibold">
                {tCommon('games.form.targets', 'Optional Targets')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {tCommon(
                  'games.form.targets.desc',
                  'Set target goals to show visual indicators in sessions',
                )}
              </p>

              <div className="grid gap-6 md:grid-cols-2 space-y-0">
                <div className="space-y-2">
                  <Label htmlFor="roundTargetPoints">
                    {tCommon('games.form.roundTargetPoints', 'Target Points Per Round (Optional)')}
                  </Label>
                  <Input
                    id="roundTargetPoints"
                    type="number"
                    {...register('roundTargetPoints', {
                      valueAsNumber: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.valueAsNumber;
                        setValue('roundTargetPoints', Number.isNaN(val) ? undefined : val);
                      },
                    })}
                    disabled={isCreating || !watchedHasRounds}
                    placeholder={watchedHasRounds ? undefined : 'Enable rounds'}
                  />
                  {watchedHasRounds && (
                    <p className="text-xs text-muted-foreground">
                      {tCommon(
                        'games.form.roundTargetPoints.desc',
                        'Show indicator when reached in a round',
                      )}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gameTargetPoints">
                    {tCommon('games.form.gameTargetPoints', 'Target Points (Game, Optional)')}
                  </Label>
                  <Input
                    id="gameTargetPoints"
                    type="number"
                    {...register('gameTargetPoints', {
                      valueAsNumber: true,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = e.target.valueAsNumber;
                        setValue('gameTargetPoints', Number.isNaN(val) ? undefined : val);
                      },
                    })}
                    disabled={isCreating}
                  />
                  <p className="text-xs text-muted-foreground">
                    {tCommon(
                      'games.form.gameTargetPoints.desc',
                      'Show indicator when total score reached',
                    )}
                  </p>
                </div>

                {watchedHasRounds && watchedWinCondition === 'mostRounds' && (
                  <div className="space-y-2">
                    <Label htmlFor="targetRounds">
                      {tCommon('games.form.targetRounds', 'Target Rounds (Optional)')}
                    </Label>
                    <Input
                      id="targetRounds"
                      type="number"
                      {...register('targetRounds', {
                        valueAsNumber: true,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.valueAsNumber;
                          setValue('targetRounds', Number.isNaN(val) ? undefined : val);
                        },
                      })}
                      disabled={isCreating}
                    />
                    <p className="text-xs text-muted-foreground">
                      {tCommon(
                        'games.form.targetRounds.desc',
                        'Show indicator when this many rounds won',
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-lg font-semibold">
                {tCommon('games.form.enableTeams', 'Enable Teams')}
              </h3>
              <RadioGroup
                value={watchedTeamScoringType}
                onValueChange={(value) => {
                  setValue('teamScoringType', value as 'none' | 'combined' | 'individual', {
                    shouldValidate: true,
                  });
                }}
                disabled={isCreating}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="team-none" />
                  <Label htmlFor="team-none" className="cursor-pointer font-normal">
                    {tCommon('games.form.teamScoringType.none', 'No Teams')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="combined" id="team-combined" />
                  <Label htmlFor="team-combined" className="cursor-pointer font-normal">
                    {tCommon('games.form.teamScoringType.combined', 'Combined')}
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground ml-6">
                  {tCommon(
                    'games.form.teamScoringType.combined.desc',
                    'Team scores as single unit',
                  )}
                </p>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="individual" id="team-individual" />
                  <Label htmlFor="team-individual" className="cursor-pointer font-normal">
                    {tCommon('games.form.teamScoringType.individual', 'Individual')}
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground ml-6">
                  {tCommon(
                    'games.form.teamScoringType.individual.desc',
                    'Players score separately',
                  )}
                </p>
              </RadioGroup>
            </div>
          </div>
        </Card>

        {/* Player Selection Section */}
        <Card className="p-6">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">{t('templates.create.players', 'Players')} *</h2>
            {isLoadingPlayers ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <p className="text-muted-foreground">{tCommon('loading', 'Loading...')}</p>
              </div>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Available Players */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      {t('sessions:create.players.available', 'Available Players')}
                    </h3>
                    {availablePlayers.length >= 6 && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder={t('sessions:create.players.search', 'Search players...')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="h-8 w-48 pl-9"
                          disabled={isCreating}
                        />
                      </div>
                    )}
                  </div>

                  {availablePlayers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border-2 border-dashed p-8">
                      <UserPlus className="h-12 w-12 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {t('sessions:create.players.noPlayers', 'No players available')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {filteredAvailablePlayers.map((player) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          isSelected={false}
                          disabled={isCreating}
                          onToggle={handlePlayerToggle}
                          onEdit={handleEditPlayer}
                        />
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingPlayerId(undefined);
                      setShowPlayerDialog(true);
                    }}
                    disabled={isCreating}
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {tCommon('players.addPlayer')}
                  </Button>
                </div>

                {/* Selected Players */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    {t('sessions:create.players.selected', 'Selected Players')} (
                    {selectedPlayers.length})
                  </h3>

                  {selectedPlayers.length === 0 ? (
                    <div className="flex min-h-[200px] flex-col items-center justify-center space-y-2 rounded-lg border-2 border-dashed p-8">
                      <p className="text-sm text-muted-foreground">
                        {t('sessions:create.players.minimum', 'Select at least one player')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {selectedPlayers.map((player) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          isSelected={true}
                          disabled={isCreating}
                          onToggle={handleRemovePlayer}
                          onEdit={handleEditPlayer}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {errors.playerIds && (
              <p className="text-sm text-destructive">{errors.playerIds.message}</p>
            )}
          </div>
        </Card>

        {/* Team Configuration Section */}
        {hasTeams && (
          <Card className="p-6">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">
                {t('templates.create.teamConfig', 'Team Configuration')}
              </h2>
              <TeamConfigurationStep
                selectedPlayers={selectedPlayers}
                teamConfiguration={teamConfiguration}
                onConfigurationChange={setTeamConfiguration}
              />
            </div>
          </Card>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isCreating} className="min-w-[120px]">
            {isCreating ? tCommon('creating', 'Creating...') : tCommon('create', 'Create')}
          </Button>
        </div>
      </form>

      <PlayerFormDialog
        open={showPlayerDialog}
        onOpenChange={(open) => {
          setShowPlayerDialog(open);
          if (!open) {
            setEditingPlayerId(undefined);
          }
        }}
        player={editingPlayerId ? allPlayers.find((p) => p.id === editingPlayerId) : undefined}
        onSuccess={(playerId) => {
          void loadPlayers();
          // Automatically add newly created player to selected players
          if (playerId) {
            const currentPlayerIds = watchedPlayerIds ?? [];
            const playerIdString = String(playerId);
            if (!currentPlayerIds.includes(playerIdString)) {
              setValue('playerIds', [...currentPlayerIds, playerIdString]);
            }
          }
        }}
      />
    </div>
  );
}
